import { InputAdornment, Slider, styled, TextField, type Theme } from '@mui/material';
import type { FieldConfigurationDto } from '@tempo-io/fim3-dto';
import { useTranslation } from 'react-i18next';
import { FlexDiv } from './CustomFieldInput';
import { FIELD_DROPDOWN_MARGIN_RIGHT, FIELD_DROPDOWN_WIDTH } from './fieldSizingConstants';

export const limitDecimalPlaces = (
  rawValue: string,
  places: number | null | undefined,
): string => {
  const maxDecimalPlaces = Math.max(places ?? 0, 0);
  const dotIndex = rawValue.indexOf('.');
  if (dotIndex === -1) return rawValue;
  if (maxDecimalPlaces === 0) return rawValue.slice(0, dotIndex);
  return rawValue.slice(0, dotIndex + 1 + maxDecimalPlaces);
};

export const isDecimalSeparatorBlocked = (
  key: string,
  places: number | null | undefined,
): boolean => Math.max(places ?? 0, 0) === 0 && (key === '.' || key === ',');

const getPercentBackgroundColor = (theme: Theme, percent: number) => {
  if (percent === 0) {
    return undefined;
  }

  if (percent >= 80) {
    return theme.palette.success.light;
  }
  if (percent >= 60) {
    return theme.palette.warning.light;
  }
  if (percent >= 30) {
    return theme.palette.warning.main;
  }
  return theme.palette.error.main;
};

const PercentSlider = styled(Slider, {
  shouldForwardProp: (prop) => prop !== 'percent',
})<{ percent: number }>(({ theme, percent }) => ({
  color: getPercentBackgroundColor(theme, percent),
  height: 24,
  borderRadius: 2,
  padding: '6px 0',
  '& .MuiSlider-track': {
    border: 'none',
  },
  '& .MuiSlider-thumb': {
    height: 24,
    width: 6,
    backgroundColor: '#fff',
    border: '1px solid currentColor',
    borderRadius: 0,
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&::before': {
      display: 'none',
    },
  },
  '& .MuiSlider-valueLabel': {
    position: 'absolute',
    top: 12,
    lineHeight: 1.2,
    fontSize: 12,
  },
}));

export const PercentFieldInput = ({
  fieldConfiguration,
  values,
  setValues,
  isEditableText,
  ...rest
}: {
  fieldConfiguration: FieldConfigurationDto;
  values: string[];
  setValues: (values: string[]) => void;
  isEditableText?: boolean;
  autoFocus?: boolean;
}) => {
  const { t: a11y } = useTranslation('translation');
  if (fieldConfiguration.fieldType !== 'PERCENT') return;

  const { places, minimum, maximum, increment } = fieldConfiguration;
  const sliderMin = minimum ?? 0;
  const sliderMax = maximum ?? 100;
  const sliderStep = increment || 1;

  const onChange = (e: Event, newValue: number | number[]) => {
    if (!e) return;
    setValues([limitDecimalPlaces(newValue.toString(), places)]);
  };
  const onTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues([limitDecimalPlaces(event.target.value, places)]);
  };
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isDecimalSeparatorBlocked(event.key, places)) {
      event.preventDefault();
    }
  };

  const numberInputOnWheelPreventChange = (e: React.WheelEvent<HTMLInputElement>) => {
    if (document.activeElement !== e.target) return;

    // Prevent the input value change
    (e.target as HTMLInputElement).blur();

    // Prevent the page/container scrolling
    e.preventDefault();
    e.stopPropagation();

    // Refocus immediately, on the next tick (after the current
    //function is done)
    setTimeout(() => {
      (e.target as HTMLInputElement).focus();
    }, 0);
  };

  const value = values[0] ? parseFloat(values[0]) : sliderMin;
  return isEditableText ? (
    <PercentSlider
      className="fs-exclude"
      data-dd-action-name="FIM 3 Custom Field - slider redacted"
      valueLabelDisplay="auto"
      onChange={onChange}
      min={sliderMin}
      max={sliderMax}
      step={sliderStep}
      percent={value}
      value={value}
      {...rest}
    />
  ) : (
    <FlexDiv>
      <TextField
        className="fs-exclude"
        data-dd-action-name="FIM 3 Custom Field - percent redacted"
        data-testid={'fim3-custom-field-input-percent-input'}
        title="" // stops html validation from showing tooltip in the input field
        value={values[0] ?? ['']}
        onChange={onTextFieldChange}
        onKeyDown={onKeyDown}
        onWheel={numberInputOnWheelPreventChange}
        type={'number'}
        size="small"
        sx={{ width: FIELD_DROPDOWN_WIDTH, flexShrink: 0, marginRight: FIELD_DROPDOWN_MARGIN_RIGHT }}
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          },
          htmlInput: {
            step: increment,
            min: minimum,
            max: maximum,
            'aria-label': a11y(($) => $.intake.a11y.percentField),
            'data-testid': 'fim3-custom-field-input-percent-input-field',
          },
        }}
        {...rest}
      />
      <Slider
        slotProps={{ input: { 'aria-label': a11y(($) => $.intake.a11y.percentSlider) } }}
        valueLabelDisplay="auto"
        value={value}
        onChange={onChange}
        min={sliderMin}
        max={sliderMax}
        step={sliderStep}
        sx={{
          '& .MuiSlider-thumb': {
            height: 24,
            width: 24,
            backgroundColor: '#fff',
            border: '2px solid currentColor',
          },
        }}
      />
    </FlexDiv>
  );
};
